import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { IUserNotification } from "../../Interface/Shared/Notification";
import NotificationService from "../../Services/Account/NotificationService";
import signalRService from "../../Services/SignalR/SignalRService";

/**
 * Reads the logged in user's notifications from Redux and keeps the unseen
 * state in sync.
 *
 * The initial load happens once after login in `useSignalR` (it is also what
 * delivers the SignalR function keys), and new notifications / seen updates
 * arrive over SignalR and are written to Redux by SignalRService. This hook
 * therefore only refreshes on demand and owns the mark-as-seen calls.
 */
export const useUserNotifications = () => {
  const isFetchingRef = useRef(false);
  const pageRef = useRef(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const notifications: IUserNotification[] = useSelector(
    (state: any) => state.Reducer?.userNotifications || []
  );

  const total: number = useSelector(
    (state: any) => state.Reducer?.notificationsTotal || 0
  );

  // Server side count: the loaded list is paged, so it can hold none of the
  // unread ones. SignalR bumps it on userNotificationReceived.
  const unreadCount: number = useSelector(
    (state: any) => state.Reducer?.unreadNotificationCount || 0
  );

  /** Reloads from the first page, replacing whatever is in the list */
  const fetchNotifications = useCallback(async () => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    try {
      await NotificationService.loadUserNotifications(1, false);
      pageRef.current = 1;
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // `total` counts every notification of the user, the list only holds the
  // pages fetched so far
  const hasMore = notifications.length > 0 && notifications.length < total;

  /** Appends the next page; drives the bell's infinite scroll */
  const loadMore = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoadingMore(true);
    const nextPage = pageRef.current + 1;
    try {
      const result = await NotificationService.loadUserNotifications(
        nextPage,
        true
      );
      // Only advance when the page actually returned something, otherwise a
      // short page would make the list skip ahead and stall
      if (result.notifications.length) {
        pageRef.current = nextPage;
      }
    } catch (error) {
      console.error("Failed to load more notifications:", error);
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hasMore]);

  const markAsSeen = useCallback(async (notificationIds: number[]) => {
    const ids = notificationIds.filter((id) => typeof id === "number");
    if (!ids.length) {
      return;
    }
    await signalRService.markNotificationsSeen(ids);
  }, []);

  /**
   * Marks every notification of the user as seen, the unloaded pages included,
   * so no ids are sent: the server resolves them from IsMarkedAllAsRead.
   */
  const markAllAsSeen = useCallback(async () => {
    await signalRService.markNotificationsSeen([], true);
  }, []);

  return {
    notifications,
    unseenCount: unreadCount,
    total,
    hasMore,
    isLoadingMore,
    fetchNotifications,
    loadMore,
    markAsSeen,
    markAllAsSeen,
  };
};

export default useUserNotifications;
